// source code from https://github.com/kentcdodds/use-deep-compare-effect under MIT license

import { useRef, useEffect, useMemo } from 'react';
import _ from 'underscore';

export const useDeepCompareMemoize = <T>(value: T): T | undefined => {
  const ref = useRef<T>();

  if (!_.isEqual(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
};

/**
 * like React.useEffect but uses deep-compare check for dependencies instead of shallow-comapre
 * @param effect Imperative function that can return a cleanup function
 * @param deps If present, effect will only activate if the values in the list change (in terms of deep-compare)
 */
export const useDeepCompareEffect: typeof useEffect = (effect, deps) => {
  useEffect(effect, useDeepCompareMemoize(deps));
};

/**
 * like React.useMemo but uses deep-compare check for dependencies instead of shallow-comapre
 * @param factory function to return a computed value
 * @param deps If present, will recompute the value if the values in the list change (in terms of deep-compare)
 */
export const useDeepCompareMemo: typeof useMemo = (factory, deps) =>
  useMemo(factory, useDeepCompareMemoize(deps));
